import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import {
  assessmentHistoryQuery,
  assessmentQuery,
  saveAssessmentAnswer,
  saveAssessmentLevel,
  type AssessmentPage,
  type AssessmentRequirement,
  type AssessmentScore,
  type AssuranceLevel,
  type MaturityStage,
} from '../api/dnscAssessment'
import { nis2DnscQuery, type DnscRequirement } from '../api/overview'
import {
  answerFields,
  answerState,
  applyAssessmentFilters,
  draftOf,
  formatScore,
  groupByCategory,
  isKeyAt,
  isManagementAt,
  LEVEL_NAMES,
  LEVEL_ORDER,
  naAllowedAt,
  needsOverrideReason,
  NO_ASSESSMENT_FILTERS,
  NO_ASSESSMENT_RIGHT,
  onForm,
  progressByFunction,
  proposalsById,
  reviewIsOld,
  toneOfAverage,
  type AnswerDraft,
  type AnswerState,
  type AssessmentFilters,
  type IntroducedFilter,
  type StatusFilter,
} from './assessment'
import { evidenceLine, toneOf } from './dnsc'
import { EvidenceUpload } from './Nis2AssessmentEvidence'
import { AssessmentExports, AssessmentRights, PlanOfMeasures } from './Nis2AssessmentPlan'

/**
 * Fila "DNSC self-assessment" din /overview/nis2: formularul DNSC (EVAL_MMS,
 * CyberFundamentals 2025) completat in platforma, cu doua note pe fiecare
 * cerinta, istoric si scorul ca in foile Excel (calculat pe server).
 *
 * Ce vede platforma (nota de implementare propusa din controalele auditului)
 * se arata langa intrebare si se pre-selecteaza - dar nu se salveaza singura.
 * Raspunsul e al firmei; semnatura e a conducerii ei.
 *
 * Textul cerintelor e citat asa cum l-a scris DNSC, in romana, ca in sectiunea
 * de sub litere; restul ecranului e in engleza.
 */

const STATE_LABEL: Record<AnswerState, string> = { todo: 'to answer', partial: 'half answered', done: 'answered', na: 'N/A' }

const STATUS_FILTERS: Array<[StatusFilter, string]> = [
  ['all', 'All'],
  ['todo', 'Still to answer'],
  ['answered', 'Answered'],
  ['below', 'Below target'],
]

export function ScorePill({ label, value, na }: { label: string; value: number | null | undefined; na?: boolean }) {
  const tone = na ? 'grey' : toneOf(value ?? null)
  return (
    <span className={`nis2-sa-pill nis2-sa-pill-${tone}`} data-sa-pill={label.toLowerCase()}>
      <span className="nis2-sa-pill-label">{label}</span> {na ? 'N/A' : value ?? '—'}
    </span>
  )
}

/** `?tab=dnsc&req=<COD>` (butonul Answer din fila Summary): cerinta de deschis la intrare. */
function reqFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('req')
  } catch {
    return null
  }
}

function LevelPicker({ page, onSaved }: { page: AssessmentPage; onSaved: () => void }) {
  const current = page.level?.level ?? null
  const [level, setLevel] = useState<AssuranceLevel | null>(current)
  const [note, setNote] = useState(page.level?.note ?? '')
  const [error, setError] = useState('')
  const counts = useMemo(() => {
    const all = page.requirements ?? []
    return (['B', 'I', 'E'] as AssuranceLevel[]).map((l) => [l, all.filter((r) => onForm(r, l)).length] as const)
  }, [page.requirements])
  const save = useMutation({
    mutationFn: () => saveAssessmentLevel(level as AssuranceLevel, note),
    onSuccess: (out) => (out.ok ? onSaved() : setError(out.error ?? 'not saved')),
    onError: (e) => setError(e instanceof Error ? e.message : 'not saved'),
  })

  return (
    <div className="nis2-sa-level-picker" data-sa-level-picker>
      <h5>Your assurance level</h5>
      <p className="nis2-muted">
        DNSC gives the level in your registration decision, from the ENIRE risk score - the platform does not guess it. It decides
        which requirements are on your form ({counts.map(([l, n]) => `${LEVEL_NAMES[l]} ${n}`).join(', ')}), how many may be
        excluded (N/A) and the targets.
      </p>
      <div className="btn-group btn-group-sm" role="group" aria-label="Assurance level">
        {counts.map(([l]) => (
          <button
            key={l}
            type="button"
            className={`btn btn-default${level === l ? ' active' : ''}`}
            aria-pressed={level === l}
            data-sa-pick={l}
            onClick={() => setLevel(l)}
          >
            {LEVEL_NAMES[l]}
          </button>
        ))}
      </div>
      <input
        className="form-control input-sm nis2-sa-level-note"
        placeholder="decision number / note (optional)"
        value={note}
        maxLength={1000}
        onChange={(e) => setNote(e.target.value)}
      />
      <button type="button" className="btn btn-primary btn-sm" disabled={level === null || save.isPending} data-sa-level-save onClick={() => save.mutate()}>
        Save level
      </button>
      {error ? <p className="nis2-sa-error">{error}</p> : null}
    </div>
  )
}

function StageChoice({
  name,
  value,
  stages,
  side,
  disabled,
  proposed,
  onChange,
}: {
  name: string
  value: number | null
  stages: MaturityStage[]
  side: 'documentation' | 'implementation'
  disabled: boolean
  proposed?: number | null
  onChange: (n: number | null) => void
}) {
  return (
    <div className="nis2-sa-stages" role="radiogroup" aria-label={side}>
      {stages.map((s) => (
        <label key={s.score} className={`nis2-sa-stage${value === s.score ? ' nis2-sa-stage-on' : ''}`} title={s[side]}>
          <input
            type="radio"
            name={name}
            value={s.score}
            checked={value === s.score}
            disabled={disabled}
            data-sa-score={`${side}-${s.score}`}
            onChange={() => onChange(s.score)}
          />
          <span className="nis2-sa-stage-n">{s.score}</span>
          <span className="nis2-sa-stage-name">
            {s.name}
            {proposed === s.score ? <span className="nis2-sa-proposed-tag">proposed from data</span> : null}
          </span>
          <span className="nis2-sa-stage-text" lang="ro">
            {s[side]}
          </span>
        </label>
      ))}
      {value !== null && !disabled ? (
        <button type="button" className="btn btn-link btn-xs" onClick={() => onChange(null)}>
          clear
        </button>
      ) : null}
    </div>
  )
}

function Seen({ proposal }: { proposal: DnscRequirement }) {
  return (
    <div className="nis2-sa-seen" data-sa-seen>
      <div className="nis2-sa-seen-head">
        <i className="fas fa-desktop" aria-hidden="true"></i> What the platform sees
        {proposal.score !== null ? (
          <span className="nis2-sa-seen-score">
            proposed implementation score: <strong data-sa-proposal={proposal.score}>{proposal.score}</strong> / 5
          </span>
        ) : (
          <span className="nis2-sa-seen-score nis2-muted">no score can be proposed from data</span>
        )}
      </div>
      <ul className="nis2-dnsc-evidence">
        {proposal.evidence.map((e) => {
          const line = evidenceLine(e)
          return (
            <li key={e.key} className={`nis2-dnsc-ev nis2-dnsc-ev-${line.state}`}>
              <i className={`fas ${line.state === 'ok' ? 'fa-check-circle' : line.state === 'warn' ? 'fa-exclamation-triangle' : line.state === 'bad' ? 'fa-times-circle' : 'fa-question-circle'}`} aria-hidden="true"></i>
              <div className="nis2-dnsc-ev-body">
                <span className="nis2-dnsc-ev-title">{e.title}</span>
                <span className="nis2-dnsc-ev-text">{line.text}</span>
                {e.sample.length > 0 ? <div className="nis2-dnsc-ev-where">where: {e.sample.join(', ')}</div> : null}
              </div>
            </li>
          )
        })}
      </ul>
      {proposal.still ? (
        <div className="nis2-dnsc-still">
          <i className="fas fa-question-circle" aria-hidden="true"></i>
          <div>
            <strong>Not visible to the platform:</strong> {proposal.still}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Editor({
  r,
  level,
  stages,
  proposal,
  onDone,
}: {
  r: AssessmentRequirement
  level: AssuranceLevel
  stages: MaturityStage[]
  proposal: DnscRequirement | undefined
  onDone: () => void
}) {
  const client = useQueryClient()
  const proposed = proposal?.score ?? null
  const [d, setD] = useState<AnswerDraft>(() => draftOf(r.answer, proposed))
  const [error, setError] = useState('')
  const set = (patch: Partial<AnswerDraft>) => setD((x) => ({ ...x, ...patch }))
  const naOk = naAllowedAt(r, level)
  const save = useMutation({
    mutationFn: () => saveAssessmentAnswer(answerFields(r.code, d, proposed)),
    onSuccess: async (out) => {
      if (!out.ok) {
        setError(out.error ?? 'not saved')
        return
      }
      await client.invalidateQueries({ queryKey: assessmentQuery.queryKey })
      await client.invalidateQueries({ queryKey: assessmentHistoryQuery(r.code).queryKey })
      onDone()
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'not saved'),
  })
  const override = needsOverrideReason(d, proposed)
  const naWhy = isKeyAt(r, level)
    ? 'a key measure cannot be excluded'
    : isManagementAt(r, level)
      ? 'a management-aspect control cannot be excluded at this level'
      : 'the DNSC form does not allow N/A here'

  return (
    <form
      className="nis2-sa-editor"
      data-sa-editor={r.code}
      onSubmit={(e) => {
        e.preventDefault()
        setError('')
        save.mutate()
      }}
    >
      <div className="nis2-sa-editor-cols">
        <fieldset>
          <legend>1. Is it written down? (documentation)</legend>
          {r.questions.documentation ? <p className="nis2-sa-question">{r.questions.documentation}</p> : null}
          {r.documentation_proposal ? (
            <p className="nis2-sa-docprop" data-sa-docprop>
              <i className="fas fa-file-alt" aria-hidden="true"></i> From {r.documentation_proposal.source}: {r.documentation_proposal.why}{' '}
              <strong>Proposed: {r.documentation_proposal.score}</strong>
            </p>
          ) : null}
          <StageChoice
            name={`doc-${r.code}`}
            value={d.doc}
            stages={stages}
            side="documentation"
            disabled={d.na}
            proposed={r.documentation_proposal?.score ?? null}
            onChange={(n) => set({ doc: n })}
          />
        </fieldset>
        <fieldset>
          <legend>2. Is it done in practice? (implementation)</legend>
          {r.questions.implementation ? <p className="nis2-sa-question">{r.questions.implementation}</p> : null}
          {proposal ? <Seen proposal={proposal} /> : null}
          {proposed !== null && !d.na && d.impl !== proposed ? (
            <button type="button" className="btn btn-default btn-xs nis2-sa-accept" data-sa-accept onClick={() => set({ impl: proposed })}>
              Accept {proposed}
            </button>
          ) : null}
          <StageChoice
            name={`impl-${r.code}`}
            value={d.impl}
            stages={stages}
            side="implementation"
            disabled={d.na}
            proposed={proposed}
            onChange={(n) => set({ impl: n })}
          />
          {override ? (
            <label className="nis2-sa-field">
              Why not the proposed {proposed}? <span className="nis2-sa-req-mark">required</span>
              <textarea
                className="form-control input-sm"
                rows={2}
                maxLength={1000}
                value={d.overrideReason}
                data-sa-override
                onChange={(e) => set({ overrideReason: e.target.value })}
              />
            </label>
          ) : null}
        </fieldset>
      </div>

      <div className="nis2-sa-na">
        {naOk ? (
          <>
            <label>
              <input type="checkbox" checked={d.na} data-sa-na onChange={(e) => set({ na: e.target.checked })} /> Does not apply to us (N/A)
            </label>
            {d.na ? (
              <label className="nis2-sa-field">
                Why <span className="nis2-sa-req-mark">required</span>
                <textarea className="form-control input-sm" rows={2} maxLength={1000} value={d.naReason} data-sa-na-reason onChange={(e) => set({ naReason: e.target.value })} />
              </label>
            ) : null}
          </>
        ) : (
          <span className="nis2-muted" data-sa-na-off>
            N/A is not possible: {naWhy}.
          </span>
        )}
      </div>

      <div className="nis2-sa-meta">
        <label className="nis2-sa-field">
          Who is responsible
          <input className="form-control input-sm" maxLength={255} value={d.responsible} onChange={(e) => set({ responsible: e.target.value })} />
        </label>
        <label className="nis2-sa-field">
          Document reviewed on
          <input type="date" className="form-control input-sm" value={d.reviewDate} data-sa-review onChange={(e) => set({ reviewDate: e.target.value })} />
          {reviewIsOld(d.reviewDate) ? <span className="nis2-sa-warn">more than 2 years ago - DNSC step 2 at most</span> : null}
        </label>
        <label className="nis2-sa-field">
          Document
          <input className="form-control input-sm" maxLength={255} placeholder="name" value={d.evidenceName} onChange={(e) => set({ evidenceName: e.target.value })} />
        </label>
        <label className="nis2-sa-field">
          Link to the document
          <input
            className="form-control input-sm"
            maxLength={1000}
            placeholder="https://... or /app/easeedesk/it_policies"
            value={d.evidenceUrl}
            onChange={(e) => set({ evidenceUrl: e.target.value })}
          />
        </label>
        <EvidenceUpload code={r.code} file={d.evidenceFile} disabled={d.na} onChange={(f) => set({ evidenceFile: f })} />
        <label className="nis2-sa-field nis2-sa-field-wide">
          Note for the assessor (optional)
          <textarea className="form-control input-sm" rows={2} maxLength={2000} value={d.note} onChange={(e) => set({ note: e.target.value })} />
        </label>
      </div>

      <div className="nis2-sa-actions">
        <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending} data-sa-save>
          Save
        </button>
        <button type="button" className="btn btn-default btn-sm" onClick={onDone}>
          Cancel
        </button>
        {error ? (
          <span className="nis2-sa-error" data-sa-error>
            {error}
          </span>
        ) : null}
      </div>
    </form>
  )
}

function History({ code }: { code: string }) {
  const q = useQuery(assessmentHistoryQuery(code))
  if (q.isPending) return <p className="nis2-muted">Reading the history...</p>
  const rows = q.data?.history ?? []
  if (rows.length === 0) return <p className="nis2-muted">no answer yet</p>
  return (
    <table className="table table-condensed nis2-sa-history" data-sa-history={code}>
      <thead>
        <tr>
          <th>When</th>
          <th>Who</th>
          <th>Documentation</th>
          <th>Implementation</th>
          <th>Reviewed</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a, i) => (
          <tr key={`${a.answered_at}-${i}`}>
            <td>{a.answered_at}</td>
            <td>{a.answered_by_email ?? '—'}</td>
            <td>{a.not_applicable ? 'N/A' : a.doc_score ?? '—'}</td>
            <td>
              {a.not_applicable ? 'N/A' : a.impl_score ?? '—'}
              {a.impl_choice === 'accepted' ? ' (accepted the proposal)' : a.impl_choice === 'override' ? ` (proposal was ${a.impl_proposed})` : ''}
            </td>
            <td>{a.review_date ?? '—'}</td>
            <td>{a.not_applicable ? a.na_reason : a.override_reason ?? a.note ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Requirement({
  r,
  level,
  stages,
  proposal,
  canAnswer,
  score,
  focus = false,
  onFocused,
}: {
  r: AssessmentRequirement
  level: AssuranceLevel
  stages: MaturityStage[]
  proposal: DnscRequirement | undefined
  canAnswer: boolean
  score: AssessmentScore | null | undefined
  focus?: boolean
  onFocused?: () => void
}) {
  const [open, setOpen] = useState<'none' | 'edit' | 'history'>('none')
  const box = useRef<HTMLDivElement>(null)
  const [scrollTo, setScrollTo] = useState(false)
  /* Legatura directa: o singura data, cu propunerea deja citita. */
  useEffect(() => {
    if (!focus) return
    if (canAnswer) setOpen('edit')
    setScrollTo(true)
    onFocused?.()
  }, [focus, canAnswer, onFocused])
  /* Derularea, in cadrul urmator: randul e desenat deja cu formularul deschis. */
  useEffect(() => {
    if (!scrollTo) return
    const frame = requestAnimationFrame(() => {
      box.current?.scrollIntoView?.({ block: 'start' })
      setScrollTo(false)
    })
    return () => cancelAnimationFrame(frame)
  }, [scrollTo, open])
  const a = r.answer
  const state = answerState(a)
  const key = isKeyAt(r, level)
  const keyScore = score?.key_measures[r.code]
  const tone = state === 'todo' ? 'grey' : state === 'na' ? 'grey' : toneOf(Math.min(a?.doc_score ?? 1, a?.impl_score ?? 1))

  return (
    <div ref={box} className={`nis2-sa-req nis2-dnsc-tone-${tone}`} data-sa-req={r.code} data-sa-state={state}>
      <div className="nis2-dnsc-req-head">
        <span className="nis2-dnsc-id">{r.code}</span>
        <span className={`nis2-dnsc-level nis2-dnsc-level-${r.level}`}>{LEVEL_NAMES[r.level]}</span>
        {key ? (
          <span className="nis2-dnsc-key" title="A key measure: its own target, and it cannot be excluded">
            <i className="fas fa-key"></i> key measure
          </span>
        ) : null}
        {isManagementAt(r, level) ? (
          <span className="nis2-dnsc-key nis2-sa-am" title="A management-aspect control: reviewed in every assurance audit">
            management aspect
          </span>
        ) : null}
        <span className={`nis2-sa-state nis2-sa-state-${state}`}>{STATE_LABEL[state]}</span>
      </div>
      <blockquote className="nis2-dnsc-quote" lang="ro">
        {r.text}
      </blockquote>
      <div className="nis2-sa-req-line">
        <ScorePill label="Documentation" value={a?.doc_score} na={a?.not_applicable} />
        <ScorePill label="Implementation" value={a?.impl_score} na={a?.not_applicable} />
        {key && keyScore && keyScore.score !== null ? (
          <span className={`nis2-sa-keyscore nis2-sa-keyscore-${keyScore.met ? 'ok' : 'below'}`}>
            key measure {formatScore(keyScore.score)} / target {keyScore.target}
          </span>
        ) : null}
        {proposal && proposal.score !== null && a?.impl_score == null && !a?.not_applicable ? (
          <span className="nis2-sa-chip" data-sa-chip>
            <i className="fas fa-desktop" aria-hidden="true"></i> data proposes {proposal.score}
          </span>
        ) : null}
        <span className="nis2-sa-req-buttons">
          {canAnswer ? (
            <button type="button" className="btn btn-default btn-xs" data-sa-open={r.code} onClick={() => setOpen(open === 'edit' ? 'none' : 'edit')}>
              {state === 'todo' ? 'Answer' : 'Change'}
            </button>
          ) : null}
          {a ? (
            <button type="button" className="btn btn-link btn-xs" onClick={() => setOpen(open === 'history' ? 'none' : 'history')}>
              history ({a.versions})
            </button>
          ) : null}
        </span>
      </div>
      {a ? (
        <div className="nis2-sa-who">
          {a.not_applicable && a.na_reason ? <>N/A: {a.na_reason}. </> : null}
          {a.responsible ? <>Responsible: {a.responsible}. </> : null}
          {a.review_date ? <>Reviewed {a.review_date}{reviewIsOld(a.review_date) ? ' (more than 2 years ago)' : ''}. </> : null}
          {a.evidence_url ? (
            <>
              Document:{' '}
              <a href={a.evidence_url} target={a.evidence_url.startsWith('/app/') ? undefined : '_blank'} rel="noreferrer">
                {a.evidence_name || a.evidence_url}
              </a>
              .{' '}
            </>
          ) : a.evidence_name ? (
            <>Document: {a.evidence_name}. </>
          ) : null}
          {a.evidence_file ? (
            <span data-sa-attached={a.evidence_file.id}>
              <i className="fas fa-paperclip" aria-hidden="true"></i> {a.evidence_file.name}.{' '}
            </span>
          ) : null}
          Answered by {a.answered_by_email ?? 'someone'}, {a.answered_at}.
        </div>
      ) : null}
      {open === 'edit' ? <Editor r={r} level={level} stages={stages} proposal={proposal} onDone={() => setOpen('none')} /> : null}
      {open === 'history' ? <History code={r.code} /> : null}
    </div>
  )
}

export function Nis2Assessment() {
  const client = useQueryClient()
  const q = useQuery(assessmentQuery)
  /* Aceeasi cerere ca sectiunea de sub litere: din cache, daca a fost deschisa. */
  const proposalsQ = useQuery({ ...nis2DnscQuery, enabled: q.data?.ready === true })
  /* Pornit din legatura directa, fara filtre: cerinta ceruta sigur se vede. */
  const [filters, setFilters] = useState<AssessmentFilters>(NO_ASSESSMENT_FILTERS)
  const [focusCode, setFocusCode] = useState<string | null>(reqFromUrl)
  const focused = useCallback(() => setFocusCode(null), [])
  const [changingLevel, setChangingLevel] = useState(false)
  const proposals = useMemo(() => proposalsById(proposalsQ.data), [proposalsQ.data])

  const page = q.data
  const level = page?.level?.level ?? null
  const requirements = useMemo(() => page?.requirements ?? [], [page?.requirements])
  const shown = useMemo(
    () => (level ? applyAssessmentFilters(requirements, level, filters, page?.score) : []),
    [requirements, level, filters, page?.score],
  )

  if (q.isPending) {
    return (
      <div className="adm-card nis2-sa" data-sa="loading">
        <p className="nis2-muted">Reading the DNSC self-assessment...</p>
      </div>
    )
  }
  if (q.isError) {
    const forbidden = q.error instanceof ApiError && q.error.status === 403
    return (
      <div className="adm-card nis2-sa" data-sa={forbidden ? 'forbidden' : 'error'}>
        <h4>DNSC self-assessment</h4>
        <p className="nis2-muted">
          <i className="fas fa-lock"></i>{' '}
          {forbidden ? NO_ASSESSMENT_RIGHT : 'The self-assessment could not be read.'}
        </p>
      </div>
    )
  }
  if (!page || !page.ready) {
    return (
      <div className="adm-card nis2-sa" data-sa="off">
        <h4>DNSC self-assessment</h4>
        <p className="nis2-muted">
          <i className="fas fa-database"></i> {page?.reason ?? 'not available on this installation'}
        </p>
      </div>
    )
  }

  const stages = page.stages ?? []
  const rules = level ? page.levels?.[level] : undefined
  const score = page.score
  const refresh = () => {
    setChangingLevel(false)
    void client.invalidateQueries({ queryKey: assessmentQuery.queryKey })
  }
  const filtered = filters.status !== 'all' || filters.keyOnly || filters.introduced !== 'all' || filters.fn !== 'all'
  const keyTotal = score ? Object.keys(score.key_measures).length : 0

  return (
    <div className="adm-card nis2-sa" data-sa="ready">
      <div className="nis2-dnsc-head">
        <h4>DNSC self-assessment - CyberFundamentals 2025</h4>
        <p className="nis2-letter-asks">
          The DNSC maturity self-assessment (EVAL_MMS, version {page.framework?.version}), filled in here: two scores of 1 to 5 on every
          requirement - is it written down, and is it done in practice - with the history of every answer. The averages, key measures
          and targets are computed exactly as in the DNSC Excel sheets. Where the platform measures the practice, its proposal is shown and
          pre-selected; the score is yours, and management signs it.
        </p>
      </div>

      {page.rights?.isAdmin ? <AssessmentRights read={page.rights.read} edit={page.rights.edit} /> : null}

      {!page.canAnswer && page.cannotAnswer ? (
        <p className="nis2-sa-readonly" data-sa-readonly>
          <i className="fas fa-eye"></i> Read only: {page.cannotAnswer}.
        </p>
      ) : null}

      {level && !changingLevel ? (
        <div className="nis2-sa-levelbar" data-sa-level={level}>
          Assurance level: <strong>{LEVEL_NAMES[level]}</strong>
          {page.level?.note ? <span className="nis2-muted"> - {page.level.note}</span> : null}
          <span className="nis2-muted">
            {' '}
            (set by {page.level?.set_by_email ?? 'someone'}, {page.level?.set_at})
          </span>
          {page.canAnswer ? (
            <button type="button" className="btn btn-link btn-xs" onClick={() => setChangingLevel(true)}>
              change
            </button>
          ) : null}
        </div>
      ) : page.canAnswer ? (
        <LevelPicker page={page} onSaved={refresh} />
      ) : (
        <p className="nis2-muted" data-sa-no-level>
          The assurance level is not set yet.
        </p>
      )}

      {level && score && rules ? (
        <>
          <div className="nis2-dnsc-summary" data-sa-summary>
            <div className={`nis2-dnsc-stat nis2-dnsc-stat-${toneOfAverage(score.total.score, score.total.target)}`}>
              <span className="nis2-dnsc-stat-value" data-sa-total>
                {formatScore(score.total.score)} <small>target {score.total.target}</small>
              </span>
              <span className="nis2-dnsc-stat-label">total maturity score</span>
            </div>
            <div className="nis2-dnsc-stat nis2-dnsc-stat-grey">
              <span className="nis2-dnsc-stat-value" data-sa-answered>
                {score.answered} <small>of {score.asked}</small>
              </span>
              <span className="nis2-dnsc-stat-label">
                answered{score.partial > 0 ? `, ${score.partial} half` : ''} - an unanswered score counts 1, as in the form
              </span>
            </div>
            <div className={`nis2-dnsc-stat nis2-dnsc-stat-${score.targets.key_measures_below.length > 0 ? 'red' : 'green'}`}>
              <span className="nis2-dnsc-stat-value" data-sa-key-below>
                {score.targets.key_measures_below.length} <small>of {keyTotal}</small>
              </span>
              <span className="nis2-dnsc-stat-label">key measures below {rules.target_key}</span>
            </div>
            <div className={`nis2-dnsc-stat nis2-dnsc-stat-${score.not_applicable.over ? 'red' : 'grey'}`}>
              <span className="nis2-dnsc-stat-value" data-sa-na-used>
                {score.not_applicable.used} <small>of {score.not_applicable.quota}</small>
              </span>
              <span className="nis2-dnsc-stat-label">excluded (N/A) - each counts {score.not_applicable.score}</span>
            </div>
          </div>

          {!score.targets.met ? (
            <p className="nis2-sa-plan" data-sa-plan>
              <i className="fas fa-exclamation-triangle"></i> The targets of the {LEVEL_NAMES[level]} level are not met
              {score.targets.categories_below.length > 0 ? ` (${score.targets.categories_below.length} categories below ${rules.target_category})` : ''}: DNSC asks
              for a plan of measures - each gap, with an action and a deadline.
            </p>
          ) : null}

          <AssessmentExports />
          <PlanOfMeasures ready={page.plan?.ready === true} rows={page.plan?.rows ?? []} canAnswer={page.canAnswer === true} />

          <div className="nis2-sa-functions" data-sa-functions>
            {progressByFunction(requirements, level, score).map((p) => (
              <button
                type="button"
                key={p.code}
                className={`nis2-sa-fn${filters.fn === p.code ? ' nis2-sa-fn-on' : ''}`}
                data-sa-fn={p.code}
                onClick={() => setFilters((f) => ({ ...f, fn: f.fn === p.code ? 'all' : p.code }))}
              >
                <span className="nis2-dnsc-fn-code">{p.code}</span> {p.name}
                <span className={`nis2-sa-fn-score nis2-sa-tone-${toneOfAverage(p.score)}`}>{formatScore(p.score)}</span>
                <span className="nis2-sa-bar">
                  <span className="nis2-sa-bar-fill" style={{ width: `${p.asked ? Math.round((p.answered * 100) / p.asked) : 0}%` }}></span>
                </span>
                <span className="nis2-sa-fn-count">
                  {p.answered} of {p.asked} answered
                </span>
              </button>
            ))}
          </div>
          <p className="nis2-sa-fn-note nis2-muted">The score per function is the average of its categories - shown for orientation only; the DNSC sheets score categories and the total.</p>

          <div className="nis2-dnsc-filters">
            <div className="btn-group btn-group-sm" role="group" aria-label="Status">
              {STATUS_FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-default${filters.status === value ? ' active' : ''}`}
                  aria-pressed={filters.status === value}
                  data-sa-status={value}
                  onClick={() => setFilters((f) => ({ ...f, status: value }))}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="btn-group btn-group-sm" role="group" aria-label="Level the requirement comes in">
              {(['all', 'B', 'I', 'E'] as IntroducedFilter[])
                .filter((v) => v === 'all' || LEVEL_ORDER[v] <= LEVEL_ORDER[level])
                .map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`btn btn-default${filters.introduced === value ? ' active' : ''}`}
                    aria-pressed={filters.introduced === value}
                    data-sa-introduced={value}
                    onClick={() => setFilters((f) => ({ ...f, introduced: value }))}
                  >
                    {value === 'all' ? 'All on the form' : LEVEL_NAMES[value]}
                  </button>
                ))}
            </div>
            <button
              type="button"
              className={`btn btn-default btn-sm${filters.keyOnly ? ' active' : ''}`}
              aria-pressed={filters.keyOnly}
              data-sa-key-only
              onClick={() => setFilters((f) => ({ ...f, keyOnly: !f.keyOnly }))}
            >
              <i className="fas fa-key"></i> Key measures only
            </button>
            <span className="nis2-dnsc-count" data-sa-count>
              {shown.length} of {score.asked} shown
              {filtered ? (
                <button type="button" className="btn btn-link btn-xs" onClick={() => setFilters(NO_ASSESSMENT_FILTERS)}>
                  clear filters
                </button>
              ) : null}
            </span>
          </div>
          {proposalsQ.isError ? (
            <p className="nis2-muted nis2-sa-noprop">The scores proposed from data could not be read for your role; answer from what you know.</p>
          ) : null}

          {shown.length === 0 ? (
            <p className="nis2-muted nis2-dnsc-none">no requirement matches these filters</p>
          ) : (
            groupByCategory(shown).map((g) => (
              <section key={g.fn.code} className="nis2-dnsc-group" data-sa-group={g.fn.code}>
                <div className="adm-section adm-section-grey nis2-dnsc-group-head">
                  <h4>
                    <span className="nis2-dnsc-fn-code">{g.fn.code}</span> {g.fn.name}
                  </h4>
                </div>
                {g.categories.map((c) => {
                  const cs = score.categories[c.code]
                  return (
                    <div key={c.code} className="nis2-sa-category" data-sa-category={c.code}>
                      <div className="nis2-sa-category-head">
                        <span className="nis2-sa-category-name" lang="ro">
                          [{c.code}] {c.name}
                        </span>
                        {cs ? (
                          <span className={`nis2-sa-category-score nis2-sa-tone-${toneOfAverage(cs.score, cs.target)}`}>
                            {formatScore(cs.score)}
                            <small>
                              {' '}
                              doc {formatScore(cs.documentation)} / impl {formatScore(cs.implementation)}
                              {cs.target !== null ? ` / target ${cs.target}` : ''}
                            </small>
                          </span>
                        ) : null}
                      </div>
                      {c.items.map((r) => (
                        <Requirement
                          key={r.code}
                          r={r}
                          level={level}
                          stages={stages}
                          proposal={proposals.get(r.code)}
                          canAnswer={page.canAnswer === true}
                          score={score}
                          focus={focusCode === r.code && !proposalsQ.isPending}
                          onFocused={focused}
                        />
                      ))}
                    </div>
                  )
                })}
              </section>
            ))
          )}
        </>
      ) : null}
    </div>
  )
}
